/**
 * <ScanAddDialog>
 * Product confirmation popup shown after a successful barcode scan.
 * Displays the product image + name and lets the operator adjust the
 * quantity (default from settings — usually 6) before adding it to the
 * invoice / draft line items.
 *
 * Props:
 *   open        boolean — controlled visibility
 *   product     resolved product (from /api/products/by-barcode/{code})
 *               or null when the scanner is showing "not found".
 *   notFoundCode  string (nullable) — if set, we render the
 *                 "Barcode not found" state and offer a rescan button.
 *   defaultQty  number — starting quantity (from settings)
 *   onAdd       (product, qty) => void — user confirmed
 *   onCancel    () => void — user dismissed
 *   onRescan    () => void — user pressed "Scan again" (for both the
 *                 not-found path AND the post-add "keep going" path)
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Minus, ScanLine, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ProductImage } from '../ProductImage';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';

const ScanAddDialog = ({
    open, product, notFoundCode, defaultQty = 6,
    onAdd, onCancel, onRescan,
}) => {
    const [qty, setQty] = useState(defaultQty);

    // Reset qty every time a new product arrives so the previous edit
    // doesn't leak into the next scan.
    useEffect(() => {
        if (product) setQty(defaultQty);
    }, [product?.id, defaultQty]);

    // Carton-based stepper (Jul 2026): +/- jumps by the operator's
    // configured carton size (defaultQty, typically 6) so pressing +
    // walks 6 → 12 → 18 → 24 instead of 6 → 7 → 8. Manual input still
    // works for arbitrary values (48, 72, 120, …).
    const cartonSize = Math.max(1, Number(defaultQty) || 6);
    const bumpUp = () => setQty((prev) => {
        const cur = Number(prev) || 0;
        // Always jump to the NEXT strict multiple of cartonSize.
        const nextMultiple = (Math.floor(cur / cartonSize) + 1) * cartonSize;
        return nextMultiple;
    });
    const bumpDown = () => setQty((prev) => {
        const cur = Number(prev) || 0;
        if (cur <= cartonSize) return cartonSize; // never below one carton
        // Round down to the previous strict multiple of cartonSize.
        const prevMultiple = (Math.ceil(cur / cartonSize) - 1) * cartonSize;
        return Math.max(cartonSize, prevMultiple);
    });

    const submit = () => {
        const n = Math.max(1, Math.floor(Number(qty) || 0));
        if (!product) return;
        onAdd(product, n);
    };

    const price = useMemo(() => {
        const p = product?.prices?.wholesale?.price ?? product?.price ?? 0;
        return Number(p || 0).toFixed(2);
    }, [product]);

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => { if (!v) onCancel(); }}
        >
            <DialogContent
                className="max-w-md p-0 overflow-hidden"
                data-testid="scan-add-dialog"
            >
                {notFoundCode ? (
                    <div className="p-6 space-y-4 text-center">
                        <div className="mx-auto w-12 h-12 rounded-full bg-[#E76F51]/10 flex items-center justify-center">
                            <X size={22} className="text-[#E76F51]" />
                        </div>
                        <DialogHeader>
                            <DialogTitle>Barcode not found</DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-gray-600">
                            No product matches barcode{' '}
                            <span className="font-mono font-semibold">{notFoundCode}</span>.
                            You can assign it from the product page or scan another item.
                        </p>
                        <DialogFooter className="flex gap-2 justify-center">
                            <Button variant="outline" onClick={onCancel} data-testid="not-found-cancel">
                                Cancel
                            </Button>
                            <Button
                                onClick={onRescan}
                                data-testid="not-found-rescan"
                                className="gap-1 bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                            >
                                <ScanLine size={14} /> Scan again
                            </Button>
                        </DialogFooter>
                    </div>
                ) : product ? (
                    <div className="p-5 space-y-4">
                        <DialogHeader className="text-left">
                            <DialogTitle className="text-base leading-snug" data-testid="scan-product-name">
                                {product.name}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex items-center gap-3">
                            <ProductImage
                                product={product}
                                alt={product.name}
                                className="w-24 h-24 bg-white rounded border"
                                testid="scan-product-image"
                            />
                            <div className="flex-1 text-xs text-gray-500 space-y-1">
                                <p>SKU <span className="font-mono text-gray-700">{product.sku}</span></p>
                                {product.brand && <p>{product.brand}</p>}
                                {price !== '0.00' && <p>Price: <span className="font-mono">{price}</span></p>}
                                {typeof product.stock_qty === 'number' && (
                                    <p>Stock: <span className="font-mono">{product.stock_qty}</span></p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                                Quantity
                            </label>
                            <div className="mt-1 flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={bumpDown}
                                    disabled={qty <= cartonSize}
                                    data-testid="qty-decrement"
                                    className="h-12 w-12"
                                >
                                    <Minus size={16} />
                                </Button>
                                {/* Manual quantity input — spec explicitly wants direct typing */}
                                <Input
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    step={1}
                                    value={qty}
                                    onChange={(e) => setQty(e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    className="h-12 text-center text-lg font-mono flex-1"
                                    data-testid="qty-input"
                                />
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={bumpUp}
                                    data-testid="qty-increment"
                                    className="h-12 w-12"
                                >
                                    <Plus size={16} />
                                </Button>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1">
                                +/- steps by <span className="font-mono font-semibold">{cartonSize}</span> (one carton).
                                Type any number to jump straight to it (e.g. <span className="font-mono">48</span>, <span className="font-mono">72</span>).
                            </p>
                        </div>

                        <DialogFooter className="flex gap-2">
                            <Button variant="outline" onClick={onCancel} data-testid="scan-cancel">
                                Cancel
                            </Button>
                            <Button
                                onClick={submit}
                                data-testid="scan-add-btn"
                                className="gap-1 bg-[#1A1A1A] hover:bg-[#C08478] text-white flex-1"
                            >
                                <Check size={14} /> Add
                            </Button>
                        </DialogFooter>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default ScanAddDialog;
